import React, { useEffect } from "react";
import { Group, Radio, Textarea } from "@mantine/core";
import { useForm, Controller } from "react-hook-form";

const ClaimQuestions = ({ setStepfour, onSubmit, claimType }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm();

  const delay = watch("delay");
  const cancel = watch("cancel");
  const overbook = watch("overbook");
  const damage = watch("damage");
  const reasonFordelay = watch("reasonFordelay");
  const reasonForcancel = watch("reasonForcancel");
  const reasonForoverbook = watch("reasonForoverbook");
  const reasonFordamage = watch("reasonFordamage");
  const issueDescription = watch("issueDescription");
  const additionalDeails = watch("additionalDeails");
  const item_cost = watch("item_cost");

  // Load saved data from localStorage on component mount
  useEffect(() => {
    const savedData = localStorage.getItem("claimQuestionsData");
    if (savedData) {
      const parsedData = JSON.parse(savedData);

      // Set form values from localStorage
      if (parsedData.delay) {
        setValue("delay", parsedData.delay);
      }
      if (parsedData.cancel) {
        setValue("cancel", parsedData.cancel);
      }
      if (parsedData.overbook) {
        setValue("overbook", parsedData.overbook);
      }
      if (parsedData.damage) {
        setValue("damage", parsedData.damage);
      }
      if (parsedData.reasonFordelay) {
        setValue("reasonFordelay", parsedData.reasonFordelay);
      }
      if (parsedData.reasonForcancel) {
        setValue("reasonForcancel", parsedData.reasonForcancel);
      }
      if (parsedData.reasonForoverbook) {
        setValue("reasonForoverbook", parsedData.reasonForoverbook);
      }
      if (parsedData.reasonFordamage) {
        setValue("reasonFordamage", parsedData.reasonFordamage);
      }
      if (parsedData.issueDescription) {
        setValue("issueDescription", parsedData.issueDescription);
      }
      if (parsedData.additionalDeails) {
        setValue("additionalDeails", parsedData.additionalDeails);
      }
      if (parsedData.item_cost) {
        setValue("item_cost", parsedData.item_cost);
      }
    }
  }, [setValue]);

  // Auto-save form data to localStorage whenever it changes
  useEffect(() => {
    const subscription = watch((value) => {
      localStorage.setItem("claimQuestionsData", JSON.stringify(value));
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // Form validation logic
  useEffect(() => {
    // Check basic required fields for each claim type
    const hasRequiredFields =
      (delay && reasonFordelay) ||
      (cancel && reasonForcancel) ||
      (overbook && reasonForoverbook) ||
      (damage && (damage !== "Delayed baggage" || reasonFordamage)) ||
      (claimType === "other" && item_cost);

    if (!hasRequiredFields) {
      setStepfour(false);
      return;
    }

    // Check additional conditions for each claim type
    const meetsAdditionalConditions =
      (delay &&
        ((reasonFordelay === "yes" && issueDescription) ||
          reasonFordelay === "no")) ||
      (cancel && (reasonForcancel === "yes" || reasonForcancel === "no")) ||
      (overbook &&
        (reasonForoverbook === "yes" || reasonForoverbook === "no")) ||
      (damage &&
        (damage !== "Delayed baggage" ||
          ["1400", "2800", "2881"].includes(reasonFordamage))) ||
      claimType === "other";

    setStepfour(meetsAdditionalConditions);
  }, [
    delay,
    reasonFordelay,
    cancel,
    reasonForcancel,
    overbook,
    reasonForoverbook,
    damage,
    reasonFordamage,
    issueDescription,
    additionalDeails,
    item_cost,
    claimType,
    setStepfour,
  ]);

  const handleFormSubmit = (data) => {
    onSubmit(data); // Pass the form data to the parent component
  };

  return (
    <div className="stepper-container claim-question-wrapper">
      <h3 className="stepper-header">Claim-specific questions & explanation</h3>
      <h6 className="sub-header" style={{ color: "#222222" }}>
        {claimType === "delayed" ? (
          <>
            Under airline regulations, you may be eligible for compensation if
            your flight was delayed by more than 3 hours due to an
            airline-related issue.
          </>
        ) : claimType === "cancelled" ? (
          <>
            You may be entitled to compensation if your flight was canceled
            within 14 days of departure and no suitable alternative was
            provided.
          </>
        ) : claimType === "denied_boarding" ? (
          <>
            If you were denied boarding due to overbooking and did not
            voluntarily give up your seat, you may qualify for compensation.
          </>
        ) : claimType === "lost/damaged" ? (
          <>
            If your baggage was lost, damaged, or delayed for more than 24
            hours, you may be entitled to reimbursement for expenses.
          </>
        ) : (
          <></> // Default case
        )}
      </h6>

      <form onSubmit={handleSubmit(handleFormSubmit)} id="claim-questions-form">
        {claimType === "delayed" && (
          <div>
            <div style={{ marginTop: "30px" }}>
              <h4 className="stepper-sub-header">
                How long was your delay?<span style={{ color: "red" }}> *</span>
              </h4>
              <div className="radio-options-wrapper">
                <Controller
                  name="delay"
                  control={control}
                  rules={{ required: "This field is required" }}
                  render={({ field }) => (
                    <Radio.Group {...field}>
                      <Radio
                        color="red"
                        variant="outline"
                        value="170"
                        label="Less than 3 hours"
                      />
                      <Radio
                        color="red"
                        variant="outline"
                        value="230"
                        label="3–4 hours"
                      />
                      <Radio
                        color="red"
                        variant="outline"
                        value="290"
                        label="4+ hours"
                      />
                    </Radio.Group>
                  )}
                />
                {errors.delay && (
                  <p style={{ color: "red" }}>{errors.delay.message}</p>
                )}
              </div>
            </div>

            <div style={{ marginTop: "30px" }}>
              <h4 className="stepper-sub-header">
                Did the airline provide a reason for the delay?
                <span style={{ color: "red" }}> *</span>
              </h4>
              <div className="radio-options-wrapper reason-delay-radio-wrapper">
                <Controller
                  name="reasonFordelay"
                  control={control}
                  rules={{ required: "This field is required" }}
                  render={({ field }) => (
                    <Radio.Group {...field}>
                      <Group>
                        <Radio
                          color="red"
                          variant="outline"
                          value="yes"
                          label="Yes"
                        />
                        <Radio
                          color="red"
                          variant="outline"
                          value="no"
                          label="No"
                        />
                      </Group>
                    </Radio.Group>
                  )}
                />
                {errors.reasonFordelay && (
                  <p style={{ color: "red" }}>
                    {errors.reasonFordelay.message}
                  </p>
                )}
              </div>
            </div>

            {reasonFordelay === "yes" && (
              <div style={{ marginTop: "20px" }}>
                <h4
                  className="sub-header"
                  style={{ color: "#222222", marginBottom: "12px" }}
                >
                  Tell us more about the issue
                  <span style={{ color: "red" }}> *</span>
                </h4>
                <Controller
                  name="issueDescription"
                  control={control}
                  rules={{ required: "This field is required" }}
                  render={({ field }) => (
                    <Textarea
                      {...field}
                      rows={5}
                      placeholder="Briefly explain what happened"
                      error={errors.issueDescription?.message}
                    />
                  )}
                />
              </div>
            )}

            <div style={{ marginTop: "30px" }}>
              <h4 className="stepper-sub-header">Additional details</h4>

              <Controller
                name="additionalDeails"
                control={control}
                render={({ field }) => (
                  <textarea
                    {...field}
                    rows={8}
                    placeholder="Briefly explain what happened"
                  />
                )}
              />
            </div>
          </div>
        )}

        {claimType === "cancelled" && (
          <div>
            <div style={{ marginTop: "30px" }}>
              <h4 className="stepper-sub-header">
                When were you informed about the cancellation?
                <span style={{ color: "red" }}> *</span>
              </h4>
              <div className="radio-options-wrapper">
                <Controller
                  name="cancel"
                  control={control}
                  rules={{ required: "This field is required" }}
                  render={({ field }) => (
                    <Radio.Group {...field}>
                      <Radio
                        color="red"
                        variant="outline"
                        value="Less than 7 days before departure"
                        label="Less than 7 days before departure"
                      />
                      <Radio
                        color="red"
                        variant="outline"
                        value="7–13 days before departure"
                        label="7–13 days before departure"
                      />
                       <Radio
                        color="red"
                        variant="outline"
                        value="14 days before departure"
                        label="14 days before departure"
                      />
                    </Radio.Group>
                  )}
                />
                {errors.cancel && (
                  <p style={{ color: "red" }}>{errors.cancel.message}</p>
                )}
              </div>
            </div>

            <div style={{ marginTop: "30px" }}>
              <h4 className="stepper-sub-header">
                Did the airline offer an alternative flight?
                <span style={{ color: "red" }}> *</span>
              </h4>
              <div className="radio-options-wrapper reason-delay-radio-wrapper">
                <Controller
                  name="reasonForcancel"
                  control={control}
                  rules={{ required: "This field is required" }}
                  render={({ field }) => (
                    <Radio.Group {...field}>
                      <Group>
                        <Radio
                          color="red"
                          variant="outline"
                          value="yes"
                          label="Yes"
                        />
                        <Radio
                          color="red"
                          variant="outline"
                          value="no"
                          label="No"
                        />
                      </Group>
                    </Radio.Group>
                  )}
                />
                {errors.reasonForcancel && (
                  <p style={{ color: "red" }}>
                    {errors.reasonForcancel.message}
                  </p>
                )}
              </div>
            </div>

            <div style={{ marginTop: "30px" }}>
              <h4 className="stepper-sub-header">Additional details</h4>

              <Controller
                name="additionalDeails"
                control={control}
                render={({ field }) => (
                  <textarea
                    {...field}
                    rows={8}
                    placeholder="Briefly explain what happened"
                  />
                )}
              />
            </div>
          </div>
        )}

        {claimType === "denied_boarding" && (
          <div>
            <div style={{ marginTop: "30px" }}>
              <h4 className="stepper-sub-header">
                Did you voluntarily give up your seat?
                <span style={{ color: "red" }}> *</span>
              </h4>
              <div className="radio-options-wrapper">
                <Controller
                  name="overbook"
                  control={control}
                  rules={{ required: "This field is required" }}
                  render={({ field }) => (
                    <Radio.Group {...field}>
                      <Radio
                        color="red"
                        variant="outline"
                        value="yes"
                        label="Yes"
                      />
                      <Radio
                        color="red"
                        variant="outline"
                        value="no"
                        label="No"
                      />
                    </Radio.Group>
                  )}
                />
                {errors.overbook && (
                  <p style={{ color: "red" }}>{errors.overbook.message}</p>
                )}
              </div>
            </div>

            <div style={{ marginTop: "30px" }}>
              <h4 className="stepper-sub-header">
                Were you offered an alternative flight?
                <span style={{ color: "red" }}> *</span>
              </h4>
              <div className="radio-options-wrapper reason-delay-radio-wrapper">
                <Controller
                  name="reasonForoverbook"
                  control={control}
                  rules={{ required: "This field is required" }}
                  render={({ field }) => (
                    <Radio.Group {...field}>
                      <Group>
                        <Radio
                          color="red"
                          variant="outline"
                          value="yes"
                          label="Yes"
                        />
                        <Radio
                          color="red"
                          variant="outline"
                          value="no"
                          label="No"
                        />
                      </Group>
                    </Radio.Group>
                  )}
                />
                {errors.reasonForoverbook && (
                  <p style={{ color: "red" }}>
                    {errors.reasonForoverbook.message}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {claimType === "lost/damaged" && (
          <div>
            <div style={{ marginTop: "30px" }}>
              <h4 className="stepper-sub-header">
                What happened to your baggage?
                <span style={{ color: "red" }}> *</span>
              </h4>
              <div className="radio-options-wrapper">
                <Controller
                  name="damage"
                  control={control}
                  rules={{ required: "This field is required" }}
                  render={({ field }) => (
                    <Radio.Group {...field}>
                      <Radio
                        color="red"
                        variant="outline"
                        value="Lost baggage"
                        label="Lost baggage"
                      />
                      <Radio
                        color="red"
                        variant="outline"
                        value="Damaged baggage"
                        label="Damaged baggage"
                      />
                      <Radio
                        color="red"
                        variant="outline"
                        value="Delayed baggage"
                        label="Delayed baggage"
                      />
                    </Radio.Group>
                  )}
                />
                {errors.damage && (
                  <p style={{ color: "red" }}>{errors.damage.message}</p>
                )}
              </div>
            </div>

            {/* Conditionally render delay duration question ONLY if "Delayed baggage" is selected */}
            {damage === "Delayed baggage" && (
              <div style={{ marginTop: "30px" }}>
                <h4 className="stepper-sub-header">
                  For delayed baggage, how long was the delay?
                  <span style={{ color: "red" }}> *</span>
                </h4>
                <div className="radio-options-wrapper">
                  <Controller
                    name="reasonFordamage"
                    control={control}
                    rules={{
                      required: "This field is required",
                      validate: (value) =>
                        damage !== "Delayed baggage" ||
                        !!value ||
                        "Delay duration is required",
                    }}
                    render={({ field }) => (
                      <Radio.Group {...field}>
                        <Radio
                          color="red"
                          variant="outline"
                          value="1400"
                          label="Less than 24 hours"
                        />
                        <Radio
                          color="red"
                          variant="outline"
                          value="2800"
                          label="24–48 hours"
                        />
                        <Radio
                          color="red"
                          variant="outline"
                          value="2881"
                          label="More than 48 hours"
                        />
                      </Radio.Group>
                    )}
                  />
                  {errors.reasonFordamage && (
                    <p style={{ color: "red" }}>
                      {errors.reasonFordamage.message}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Rest of the form (item cost) */}
            <div style={{ marginTop: "30px" }}>
              <h4 className="stepper-sub-header">
                What is the estimated value of lost or damaged items?
              </h4>
              Estimated amount
              <div className="radio-options-wrapper reason-delay-radio-wrapper">
                <div className="radio-options-wrapper" style={{ width: "40%" }}>
                  <Controller
                    name="item_cost"
                    control={control}
                    render={({ field }) => (
                      <input
                        {...field}
                        className="input-box"
                        placeholder="Enter the amount"
                        type="text"
                        onChange={(e) => {
                          const input = e.target.value;

                          // Allow only numbers, one dot, and optional ₹ or $
                          if (
                            /^(\₹|\$)?\d{0,7}(\.\d{0,2})?$/.test(input) ||
                            input === ""
                          ) {
                            setValue("item_cost", input);
                          }
                        }}
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {claimType === "other" && (
          <div>
            <div style={{ marginTop: "30px" }}>
              <h4 className="stepper-sub-header">
                What is the estimated value of Compensation
                <span style={{ color: "red" }}> *</span>
              </h4>
              <div className="radio-options-wrapper reason-delay-radio-wrapper">
                <div className="radio-options-wrapper" style={{ width: "40%" }}>
                  <Controller
                    name="item_cost"
                    control={control}
                    rules={{ required: "This field is required" }}
                    render={({ field }) => (
                      <input
                        {...field}
                        className="input-box"
                        placeholder="Enter the amount"
                        type="text"
                        onChange={(e) => {
                          const input = e.target.value;

                          // Allow only numbers, one dot, and optional ₹ or $
                          if (
                            /^(\₹|\$)?\d{0,7}(\.\d{0,2})?$/.test(input) ||
                            input === ""
                          ) {
                            setValue("item_cost", input);
                          }
                        }}
                      />
                    )}
                  />
                </div>
                {errors.item_cost && (
                  <p style={{ color: "red" }}>{errors.item_cost.message}</p>
                )}
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default ClaimQuestions;
